import { Link } from 'react-router-dom';
import { useDocumentTitle } from '../lib/useDocumentTitle';

/**
 * Privacy policy — surfaced at /privacy. Linked from:
 *   - Signup checkbox ("agreeTerms")
 *   - Chrome Web Store listing form (required)
 *   - Site footer / extension login
 *
 * Content reviewed against:
 *   - Taiwan 個人資料保護法 (PDPA)
 *   - EU GDPR (for visitors in scope)
 *   - Chrome Web Store program policies (data-use disclosure)
 */
export function Privacy() {
  useDocumentTitle('隱私權政策 — NOXOR');
  const lastUpdated = '2026-05-22';

  return (
    <LegalShell title="隱私權政策" lastUpdated={lastUpdated}>
      <p>
        本文件說明 NOXOR（「本服務」、「我們」）如何蒐集、使用、儲存與分享你的
        個人資料。當你使用 noxor.live 網站、註冊帳號、與 AI 角色互動，或安裝
        我們的 Chrome 擴充功能 NOXOR Pet 時，即代表你已閱讀並同意本政策。
      </p>

      <Section title="1. 我們蒐集的資料">
        <H>1.1 你主動提供的資料</H>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>帳號資料</b>：電子郵件、密碼（以雜湊形式儲存）、顯示名稱、頭像（選填）。</li>
          <li><b>個人檔案</b>：暱稱、生日、性別（皆為選填，可隨時修改或清空）。</li>
          <li><b>對話內容</b>：你與 AI 角色之間的訊息與生成的回覆。為了讓角色保有上下文記憶，我們會儲存最近的對話紀錄與系統摘要。</li>
          <li><b>語音資料</b>（若啟用語音通話）：你的麥克風音訊會被傳送到語音辨識（ASR）服務，轉成文字後即刻丟棄；文字才會被保留以供 AI 回應。</li>
          <li><b>付款資訊</b>（若啟用儲值功能）：由第三方金流商（如 ECPay）處理，我們僅儲存交易結果與點數餘額，不接觸或儲存信用卡號。</li>
        </ul>
        <H>1.2 自動蒐集的技術資料</H>
        <ul className="list-disc pl-5 space-y-1">
          <li>IP 位址、瀏覽器 / 裝置類型、語系、操作時戳。</li>
          <li>瀏覽器本地儲存（localStorage / IndexedDB）：登入 token、語系偏好、UI 設定。</li>
          <li>CloudFront / API Gateway 存取日誌：保留 90 天以供安全分析與計費。</li>
        </ul>
        <H>1.3 Chrome 擴充功能額外蒐集的資料</H>
        <p>
          NOXOR Pet 擴充功能在啟用「摘要當前頁面」功能時，會將你**目前所在分頁的 URL
          與可見文字內容**送至我們的 AI 服務以產生摘要。請僅在你信任的網頁上使用此功能。
          摘要結果可能會儲存在你的對話紀錄中，與一般聊天訊息相同處理。
        </p>
      </Section>

      <Section title="2. 我們如何使用資料">
        <ul className="list-disc pl-5 space-y-1">
          <li>提供與維護核心服務（帳號管理、與角色聊天、語音通話、頁面摘要等）。</li>
          <li>讓角色記住你（基於對話紀錄產生個人化記憶摘要）、累積好感度。</li>
          <li>處理你的儲值、消費點數、結算經營者收益。</li>
          <li>偵測異常行為與防範濫用（如機器人登入、垃圾訊息）。</li>
          <li>產品改善（彙整後的匿名統計，例如「8 成使用者每日聊天少於 10 則」）。</li>
          <li>當你主動聯絡客服或回報問題時，回覆與後續追蹤。</li>
        </ul>
        <p className="mt-3">
          我們<b>不會將你的個人資料販售給第三方</b>，也不會用於行銷廣告投放。
        </p>
      </Section>

      <Section title="3. 第三方服務（資料處理者）">
        <p>
          為了提供服務，部分資料會傳送至下列第三方。所有資料皆透過 HTTPS / TLS 1.2+
          加密傳輸：
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Amazon Web Services（AWS）</b> — Tokyo (ap-northeast-1) 區域。儲存帳號、對話、點數等所有靜態資料；使用 DynamoDB、S3、Cognito、CloudFront。</li>
          <li><b>OpenAI 或其他大型語言模型（LLM）供應商</b> — 你的訊息會被傳送以產生 AI 回覆。請參考 <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline">OpenAI 隱私權政策</a>。</li>
          <li><b>語音合成 / 辨識服務</b>（VoxCPM、VOAI、瀏覽器內建 ASR 等）— 視角色設定而定。僅傳送該次必要的文字 / 音訊片段。</li>
          <li><b>Google Sign-In</b>（若啟用）— 你選擇使用 Google 帳號登入時，Google 會提供 email、姓名、頭像予我們。</li>
          <li><b>ECPay 綠界</b>（若啟用儲值）— 處理信用卡 / 超商代碼等付款流程。</li>
        </ul>
      </Section>

      <Section title="4. 資料保留與刪除">
        <ul className="list-disc pl-5 space-y-1">
          <li><b>帳號資料</b>：你刪除帳號的瞬間立即從主要資料庫移除（DynamoDB 即時刪除；S3 物件於 30 天內清除）。</li>
          <li><b>對話紀錄與記憶摘要</b>：帳號刪除時一併移除；你也可以個別清除單一角色的對話紀錄。</li>
          <li><b>付款 / 點數紀錄</b>：為了會計與稅務目的，交易明細保留 5 年（依台灣商業會計法）。</li>
          <li><b>CloudFront 存取日誌</b>：90 天後自動清除。</li>
        </ul>
      </Section>

      <Section title="5. 你的權利">
        <p>依個人資料保護法與 GDPR（適用範圍內），你享有以下權利：</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>查詢</b>你的資料 — 登入後在「個人資料」頁面可看到所有欄位。</li>
          <li><b>修改</b>不正確的資料 — 同上頁面隨時編輯。</li>
          <li><b>刪除</b>整個帳號 — 「個人資料 → 刪除帳號」按鈕，操作不可逆。</li>
          <li><b>下載</b>你的資料副本 — 寄信至下方聯絡信箱要求匯出，我們將於 14 天內提供 JSON 檔。</li>
          <li><b>撤回同意</b> — 你可隨時撤回對本政策的同意，方式為刪除帳號。</li>
        </ul>
      </Section>

      <Section title="6. 安全措施">
        <ul className="list-disc pl-5 space-y-1">
          <li>所有 API 流量透過 HTTPS / TLS 1.2+ 加密。</li>
          <li>密碼以 AWS Cognito 的 SRP（Secure Remote Password）協定處理，本服務伺服器永遠不接觸明文密碼。</li>
          <li>DynamoDB 預設啟用靜態加密（AWS-owned KMS key）。</li>
          <li>S3 bucket 不公開、僅透過 Origin Access Control 與 CloudFront 提供。</li>
          <li>定期執行安全稽核與滲透測試。</li>
        </ul>
      </Section>

      <Section title="7. Cookie 與本地儲存">
        <p>
          本服務不使用追蹤型 cookie。瀏覽器的 localStorage 僅用於：
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>儲存登入 token（JWT，有效 60 分鐘）與 refresh token（30 天）。</li>
          <li>記住你的語系偏好（zh-TW / en）。</li>
          <li>暫存 UI 狀態（例如最近選擇的角色）。</li>
        </ul>
        <p className="mt-2">你可以隨時透過瀏覽器設定清除本地儲存，這會強制重新登入。</p>
      </Section>

      <Section title="8. 兒童與未成年人">
        <p>
          本服務不主動針對 13 歲以下兒童。如果你是未成年人，請取得家長或監護人同意
          後再使用。我們不會在知情下蒐集 13 歲以下使用者的個人資料。
        </p>
      </Section>

      <Section title="9. 政策修改">
        <p>
          當本政策有實質變更時（例如新增第三方服務、變更資料保留期），我們會：
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>在本頁更新「最後修訂日期」。</li>
          <li>以站內公告或 email 通知所有註冊使用者。</li>
          <li>重大變更會在生效前提供至少 14 天的緩衝期。</li>
        </ul>
      </Section>

      <Section title="10. 聯絡我們">
        <p>對本政策有疑問、想要行使任何權利、或回報資安事件，請聯絡：</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Email：<a href="mailto:sct@sparklecloud.co" className="underline">sct@sparklecloud.co</a></li>
          <li>站內：<Link to="/me" className="underline">登入後個人資料頁</Link></li>
        </ul>
        <p className="mt-2 text-slate-400 text-xs">
          資料控制者：NOXOR 平台營運團隊（台灣，台北市）。
        </p>
      </Section>
    </LegalShell>
  );
}

/* ── Shared layout / typography for legal pages ── */
function LegalShell({
  title,
  lastUpdated,
  children,
}: {
  title: string;
  lastUpdated: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="min-h-screen px-4 py-8 sm:py-12"
      style={{ background: 'var(--noxor-bg)', color: 'var(--noxor-text)' }}
    >
      <div className="mx-auto w-full max-w-3xl space-y-6">
        <div className="flex items-center justify-between">
          <Link
            to="/"
            className="text-sm text-slate-400 hover:text-slate-200"
          >
            ← 回首頁
          </Link>
          <div className="text-xs text-slate-500">
            最後更新：{lastUpdated}
          </div>
        </div>
        <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
        <div className="rounded-2xl border p-6 sm:p-8 space-y-6 text-sm leading-relaxed"
             style={{ borderColor: 'var(--noxor-border)', background: 'rgba(7,19,47,0.6)' }}>
          {children}
        </div>
        <footer className="text-center text-xs text-slate-500 pt-4">
          <Link to="/terms" className="underline hover:text-slate-300 mx-2">服務條款</Link>
          <span>·</span>
          <Link to="/privacy" className="underline hover:text-slate-300 mx-2">隱私權政策</Link>
        </footer>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-lg font-semibold border-b pb-1" style={{ borderColor: 'var(--noxor-border)' }}>{title}</h2>
      <div className="space-y-2 text-slate-200">{children}</div>
    </section>
  );
}

function H({ children }: { children: React.ReactNode }) {
  return <h3 className="text-sm font-semibold mt-3 text-slate-100">{children}</h3>;
}

export { LegalShell, Section, H };
